import React, { useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import SeatIcon from "@mui/icons-material/EventSeat";
import LuggageIcon from "@mui/icons-material/Luggage";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import SeatMap from "../seatMap/SeatMap";
import { SeatData } from "../seatMap/types";
import NoDataFlightCard from "../NoDataFlightCard";
import SeatSkeleton from "./SeatSkeleton";
import FlightClassIcon from "@mui/icons-material/FlightClass";
import {
  calculateTotalFare,
  extractLegsFromJourney,
  formatLegDisplay,
  getFlightNumber,
  getSeatDisplayInfo,
} from "../../../constants/helper";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
type FlightSeatInfoProps = {
  totalTravellers?: number;
  meal?: any[];
  baggage: any[];
  seatMapData: any[];
  isSeatMapLoading?: boolean;
  otherCharges?: (charges: any) => void;
  journeys?: any[];
  isIntl?: boolean;
  activeJourneyIndex?: (index: number) => void;
  selectedSeatsByJourney: {
    [journeyIndex: number]: {
      [legIndex: number]: any[];
    };
  };
  setSelectedSeatsByJourney: React.Dispatch<
    React.SetStateAction<{
      [journeyIndex: number]: {
        [legIndex: number]: any[];
      };
    }>
  >;
};

const FlightSeatInfo = ({
  isSeatMapLoading,
  baggage,
  meal,
  isIntl,
  totalTravellers = 1,
  seatMapData,
  otherCharges,
  journeys,
  activeJourneyIndex,
  selectedSeatsByJourney,
  setSelectedSeatsByJourney,
}: FlightSeatInfoProps) => {
  const [value, setValue] = useState(0);
  const [mealCounts, setMealCounts] = useState<number[]>([]);
  const [baggageCounts, setBaggageCounts] = useState<number[]>([]);
  const [flightNumber, setFlightNumber] = useState<string | null>(null);
  const [selectedLegIndex, setSelectedLegIndex] = useState<{
    journeyIndex: number;
    legIndex: number;
    flightNum: Number;
  } | null>(null);
  const [allMealSelections, setAllMealSelections] = useState<{
    [journeyIndex: number]: { [legIndex: number]: number[] };
  }>({});

  const [allBaggageSelections, setAllBaggageSelections] = useState<{
    [journeyIndex: number]: { [legIndex: number]: number[] };
  }>({});
  const [activeJourneyIndexx, setActiveJourneyIndexx] = useState(0);
  const [totals, setTotals] = useState({
    seat: 0,
    meal: 0,
    baggage: 0,
    grandTotal: 0,
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  useEffect(() => {
    if (
      journeys?.length &&
      selectedLegIndex === null &&
      journeys[0]?.Segments?.[0]?.Bonds?.[0]?.Legs?.length
    ) {
      const defaultLeg = journeys[0].Segments[0].Bonds[0].Legs[0];
      setSelectedLegIndex({
        journeyIndex: 0,
        legIndex: 0,
        flightNum: defaultLeg.FlightNumber,
      });
      setActiveJourneyIndexx(0);
      activeJourneyIndex?.(0);
    }
  }, [journeys, selectedLegIndex]);

  const selectedSeats =
    selectedSeatsByJourney?.[selectedLegIndex?.journeyIndex ?? 0]?.[
      selectedLegIndex?.legIndex ?? 0
    ] ?? [];
  useEffect(() => {
    const seatTotal = Object.values(selectedSeatsByJourney).reduce(
      (journeySum, legs) =>
        journeySum +
        Object.values(legs).reduce(
          (legSum, seats) =>
            legSum +
            seats.reduce((s, seat) => s + (seat?.SeatFare?.TotalFare ?? 0), 0),
          0
        ),
      0
    );

    const mealTotal = Object.entries(allMealSelections).reduce(
      (journeySum, [jIndex, legs]) =>
        journeySum +
        Object.entries(legs).reduce(
          (legSum, [lIndex, counts]) =>
            legSum +
            counts.reduce(
              (sum, count, i) => sum + count * (meal?.[i]?.Amount || 0),
              0
            ),
          0
        ),
      0
    );

    const baggageTotal = Object.entries(allBaggageSelections).reduce(
      (journeySum, [jIndex, legs]) =>
        journeySum +
        Object.entries(legs).reduce(
          (legSum, [lIndex, counts]) =>
            legSum +
            counts.reduce(
              (sum, count, i) => sum + count * (baggage?.[i]?.Amount || 0),
              0
            ),
          0
        ),
      0
    );

    // Get all selected seats from all journeys
    const allSelectedSeats = Object.entries(selectedSeatsByJourney).flatMap(
      ([journeyIndex, legs]) =>
        Object.entries(legs).flatMap(([legIndex, seats]) =>
          seats.map((seat) => ({
            ...seat,
            journeyIndex: Number(journeyIndex),
            legIndex: Number(legIndex),
            flightNumber: !isIntl
              ? journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[0]
                  ?.Legs?.[Number(legIndex)]?.FlightNumber
              : journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[
                  legIndex
                ]?.Legs?.[
                  Number(
                    journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[
                      legIndex
                    ]?.Legs?.length === 1
                      ? 0
                      : legIndex
                  )
                ]?.FlightNumber,
          }))
        )
    );
    const allSelectedMeals = Object.entries(allMealSelections).flatMap(
      ([journeyIndex, legs]) =>
        Object.entries(legs).flatMap(([legIndex, counts]) =>
          counts
            .map((count, i) => ({
              ...meal?.[i],
              count,
              journeyIndex: Number(journeyIndex),
              legIndex: Number(legIndex),
              flightNumber: !isIntl
                ? journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[0]
                    ?.Legs?.[Number(legIndex)]?.FlightNumber
                : journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[
                    legIndex
                  ]?.Legs?.[
                    Number(
                      journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[
                        legIndex
                      ]?.Legs?.length === 1
                        ? 0
                        : legIndex
                    )
                  ]?.FlightNumber,
              totalAmount: count * (meal?.[i]?.Amount || 0),
            }))
            .filter((m) => m.count > 0)
        )
    );

    const allSelectedBaggage = Object.entries(allBaggageSelections).flatMap(
      ([journeyIndex, legs]) =>
        Object.entries(legs).flatMap(([legIndex, counts]) =>
          counts
            .map((count, i) => ({
              ...baggage?.[i],
              count,
              journeyIndex: Number(journeyIndex),
              legIndex: Number(legIndex),
              flightNumber: !isIntl
                ? journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[0]
                    ?.Legs?.[Number(legIndex)]?.FlightNumber
                : journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[
                    legIndex
                  ]?.Legs?.[
                    Number(
                      journeys?.[Number(journeyIndex)]?.Segments?.[0]?.Bonds?.[
                        legIndex
                      ]?.Legs?.length === 1
                        ? 0
                        : legIndex
                    )
                  ]?.FlightNumber,
              totalAmount: count * (baggage?.[i]?.Amount || 0),
            }))
            .filter((b) => b.count > 0)
        )
    );

    const totalCharges = {
      seat: seatTotal,
      meal: mealTotal,
      baggage: baggageTotal,
      grandTotal: seatTotal + mealTotal + baggageTotal,
      selectedMeal: allSelectedMeals,
      selectedBaggage: allSelectedBaggage,
      selectedSeats: allSelectedSeats,
      flightNumber: selectedLegIndex?.flightNum,
    };

    otherCharges?.(totalCharges);
    setTotals(totalCharges);
  }, [
    selectedSeatsByJourney,
    allMealSelections,
    allBaggageSelections,
    selectedLegIndex?.flightNum,
    journeys,
    meal,
    baggage,
  ]); // Added journeys to dependency array

  const handleSeatSelect = (seat: SeatData) => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null) return;

    setSelectedSeatsByJourney((prev) => {
      const journey = prev[journeyIndex] || {};
      const legSeats = journey[legIndex] || [];

      const isSelected = legSeats.some(
        (s: SeatData) => s.SeatNumber === seat.SeatNumber
      );

      const updatedSeats = isSelected
        ? legSeats.filter((s) => s.SeatNumber !== seat.SeatNumber)
        : legSeats.length >= totalTravellers
        ? legSeats
        : [...legSeats, seat];
      return {
        ...prev,
        [journeyIndex]: {
          ...journey,
          [legIndex]: updatedSeats,
        },
      };
    });
  };

  useEffect(() => {
    setMealCounts(Array(meal?.length).fill(0));
  }, [meal]);

  useEffect(() => {
    setBaggageCounts(Array(baggage?.length).fill(0));
  }, [baggage]);

  const totalMealSelected = mealCounts.reduce((a, b) => a + b, 0);
  const totalSelected = baggageCounts.reduce((a, b) => a + b, 0);

  const handleMealAdd = (index: number) => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null) return;

    // Check total across all journeys
    const totalMealSelected = Object.values(allMealSelections).reduce(
      (journeySum, legs) =>
        journeySum +
        Object.values(legs).reduce(
          (legSum, counts) => legSum + counts.reduce((a, b) => a + b, 0),
          0
        ),
      0
    );

    if (totalMealSelected >= totalTravellers) return;

    setAllMealSelections((prev) => {
      const journey = prev[journeyIndex] || {};
      const legCounts = journey[legIndex] || Array(meal?.length).fill(0);
      const updated = [...legCounts];
      updated[index]++;

      return {
        ...prev,
        [journeyIndex]: {
          ...journey,
          [legIndex]: updated,
        },
      };
    });
  };

  const handleMealSubtract = (index: number) => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null) return;

    setAllMealSelections((prev) => {
      const journey = prev[journeyIndex] || {};
      const legCounts = journey[legIndex] || Array(meal?.length).fill(0);

      if (legCounts[index] > 0) {
        const updated = [...legCounts];
        updated[index]--;

        return {
          ...prev,
          [journeyIndex]: {
            ...journey,
            [legIndex]: updated,
          },
        };
      }
      return prev;
    });
  };

  const handleAdd = (index: number) => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null) return;

    // Check total across all journeys
    const totalBaggageSelected = Object.values(allBaggageSelections).reduce(
      (journeySum, legs) =>
        journeySum +
        Object.values(legs).reduce(
          (legSum, counts) => legSum + counts.reduce((a, b) => a + b, 0),
          0
        ),
      0
    );

    if (totalBaggageSelected >= totalTravellers) return;

    setAllBaggageSelections((prev) => {
      const journey = prev[journeyIndex] || {};
      const legCounts = journey[legIndex] || Array(baggage?.length).fill(0);
      const updated = [...legCounts];
      updated[index]++;

      return {
        ...prev,
        [journeyIndex]: {
          ...journey,
          [legIndex]: updated,
        },
      };
    });
  };
  const handleSubtract = (index: number) => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null) return;

    setAllBaggageSelections((prev) => {
      const journey = prev[journeyIndex] || {};
      const legCounts = journey[legIndex] || Array(baggage?.length).fill(0);

      if (legCounts[index] > 0) {
        const updated = [...legCounts];
        updated[index]--;

        return {
          ...prev,
          [journeyIndex]: {
            ...journey,
            [legIndex]: updated,
          },
        };
      }
      return prev;
    });
  };
  const getCurrentLegMealCounts = () => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null)
      return Array(meal?.length).fill(0);

    return (
      allMealSelections[journeyIndex]?.[legIndex] || Array(meal?.length).fill(0)
    );
  };

  const getCurrentLegBaggageCounts = () => {
    const { journeyIndex, legIndex } = selectedLegIndex || {};
    if (journeyIndex == null || legIndex == null)
      return Array(baggage?.length).fill(0);

    return (
      allBaggageSelections[journeyIndex]?.[legIndex] ||
      Array(baggage?.length).fill(0)
    );
  };

  const isMealAvailble = !!meal && meal?.length > 0;
  const isBaggageAvailble = !!baggage && baggage?.length > 0;

  return (
    <div className="card my-3 addons-service-card">
      <div className="card-header card-head-bg">Addons Service</div>
      <div className="card-body">
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              scrollButtons="auto"
              aria-label="basic tabs example"
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab
                icon={<SeatIcon />}
                label="Seat"
                {...a11yProps(0)}
                iconPosition="start"
              />
              <Tab
                icon={<LuggageIcon />}
                label="Baggage"
                {...a11yProps(1)}
                iconPosition="start"
              />
              {/* <Tab label="Popular Add-Ons" {...a11yProps(2)} /> */}
              <Tab
                icon={<FastfoodIcon />}
                label="Meal"
                {...a11yProps(2)}
                iconPosition="start"
              />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <div className="row">
              <div className="col-md-4">
                <div className="container">
                  <div className="row">
                    <>
                      {/* Journey Legs Selection */}
                      {!!journeys?.length && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {journeys.map((journey, journeyIndex) => {
                            const legs = extractLegsFromJourney(journey);

                            return legs.map((leg: any, legIndex: number) => {
                              const isActive =
                                selectedLegIndex?.journeyIndex ===
                                  journeyIndex &&
                                selectedLegIndex?.legIndex === legIndex;

                              const { display, subtitle } =
                                formatLegDisplay(leg);

                              return (
                                <button
                                  key={`${journeyIndex}-${legIndex}`}
                                  onClick={() => {
                                    const flightNum =
                                      leg?.FlightNumber || leg?.FlightNo;

                                    setSelectedLegIndex({
                                      journeyIndex,
                                      legIndex,
                                      flightNum,
                                    });
                                    setActiveJourneyIndexx?.(journeyIndex);
                                    activeJourneyIndex?.(journeyIndex);
                                  }}
                                  className={`px-4 py-2 rounded-full border font-semibold text-sm transition-all duration-200 hover:scale-105  ${
                                    isActive
                                      ? "seat-btn-color  text-white"
                                      : "text-dark border border-dark bg-transparent "
                                  }`}
                                  title={subtitle || undefined}
                                >
                                  <div className="flex flex-col items-center">
                                    <span>{display}</span>
                                  </div>
                                </button>
                              );
                            });
                          })}
                        </div>
                      )}

                      {/* Selected Seats Summary */}
                      {selectedSeats?.length > 0 && (
                        <div className="airline-card mt-3">
                          <div className="fare-box mt-3">
                            <hr />

                            <div className="py-2 space-y-2 text-sm max-h-40 overflow-y-auto">
                              {selectedSeats.map((seat, index) => {
                                const { seatNumber, fare } =
                                  getSeatDisplayInfo(seat);

                                return (
                                  <div
                                    key={`${seat?.id || index}`}
                                    className="flex justify-between items-center group hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        Seat {seatNumber}
                                      </span>
                                      {seat?.SeatType && (
                                        <span className="text-xs text-gray-500">
                                          {seat.SeatType}
                                        </span>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">
                                        ₹{fare}
                                      </span>
                                      <button
                                        onClick={() => {
                                          const updatedSeats =
                                            selectedSeats.filter(
                                              (_, i) => i !== index
                                            );
                                        }}
                                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs transition-opacity"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            <hr className="my-2" />

                            <div className="total-fare pt-2 font-semibold flex justify-between items-center">
                              <span>Total Fare</span>
                              <span className="text-lg">
                                ₹
                                {calculateTotalFare(
                                  selectedSeats
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  </div>
                  <div className="row mt-4">
                    <h5 className="mb-3">
                      <i className="fas fa-chair" /> Seat Type
                    </h5>
                    <div className="seat-type">
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#a6f3d5" }}
                        />
                        Free
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#b2d9ff" }}
                        />
                        ₹0-200
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#1d76db" }}
                        />
                        ₹201-400
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#d13ff0" }}
                        />
                        ₹401-500
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#f3c07b" }}
                        />
                        ₹501-1200
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#e8c1a0" }}
                        />
                        ₹1201-1399
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#d5b5ff" }}
                        />
                        ₹1400-1499
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#981e74" }}
                        />
                        ₹1500-3000
                      </div>
                      <div className="seat-box">
                        <span
                          className="seat-color"
                          style={{ backgroundColor: "#cb64b0" }}
                        />
                        ₹Above 3000
                      </div>
                    </div>
                    <div className="extra-options">
                      <div className="box">
                        <span className="symbol">XL</span> Extra Legroom
                      </div>
                      <div className="box">
                        <span
                          className="symbol"
                          style={{ backgroundColor: "#f0f0f0" }}
                        />{" "}
                        Non Reclining
                      </div>
                      <div className="box">
                        <span
                          className="symbol"
                          style={{ backgroundColor: "#ff4d4d" }}
                        />{" "}
                        Exit Row Seats
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className=" col-md-8">
                {isSeatMapLoading ? (
                  <SeatSkeleton />
                ) : seatMapData?.length !== 0 ? (
                  <div className="scrollDiv relative">
                    {/* Background Image */}
                    <div
                      style={{ width: "390px", top: "-36px" }}
                      className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full max-w-md "
                    >
                      <img
                        src="media/images/airoplan-01.png"
                        alt="ewts-booking"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    {/* Foreground Content */}
                    <div className="flightWrap ng-scope relative ">
                      <div
                        className="min-h-screen"
                        style={{ marginTop: "200px" }}
                      >
                        <div className="max-w-3xl mx-auto flight-border-class">
                          {/* <h1 className="text-2xl font-bold text-gray-900 mb-6">
                        Select Your Seat
                      </h1> */}
                          <SeatMap
                            seatMap={seatMapData?.filter(
                              (item) =>
                                item?.FlightNumber ===
                                selectedLegIndex?.flightNum
                            )}
                            onSeatSelect={handleSeatSelect}
                            selectedSeats={selectedSeats}
                            totalTravellers={totalTravellers}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <NoDataFlightCard
                    headingText="No seat Found"
                    icon={
                      <FlightClassIcon className="no-data-color me-2 mr-2 flex-shrink-0 mt-0.5" />
                    }
                  />
                )}
              </div>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <div className="row">
              {isBaggageAvailble ? (
                baggage?.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between gap-3 col-md-6 border"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="img_sec lightbluebg">
                        <img
                          src={item.ImageURL || "media/images/3Kg-bag.png"}
                          width={80}
                          alt="Baggage"
                        />
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <span className="text-2sm font-medium text-gray-900 cursor-pointer hover:text-primary mb-px">
                          {item.Detail || "N/A"}
                        </span>
                        <span className="text-2xs text-gray-700">
                          <i className="fa-solid fa-indian-rupee-sign"></i>
                          {item.Amount || 0}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5">
                      <div className="PlusMinusRow_adon">
                        <a
                          type="button"
                          className={`sub_adon subanc ${
                            getCurrentLegBaggageCounts()[index] === 0
                              ? "opacity-50 pointer-events-none"
                              : ""
                          }`}
                          onClick={() => handleSubtract(index)}
                        >
                          <span>-</span>
                        </a>
                        <input
                          type="text"
                          value={getCurrentLegBaggageCounts()[index]}
                          readOnly
                          className="PlusMinus_number_adon"
                        />
                        <a
                          type="button"
                          className={`add_adon addanc ${
                            totalSelected >= totalTravellers
                              ? "opacity-50 pointer-events-none"
                              : ""
                          }`}
                          onClick={() => handleAdd(index)}
                        >
                          <span>+</span>
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoDataFlightCard
                  headingText="No Baggage Found "
                  icon={
                    <LuggageIcon className=" me-2 no-data-color flex-shrink-0 mt-0.5" />
                  }
                  paragraph=""
                />
              )}
            </div>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <div className="row coupons-list scrollable-y-auto">
              {isMealAvailble ? (
                meal?.map((item, index) => (
                  <div key={index} className=" col-md-6 border  mb-3">
                    <div className="row ">
                      <div className="flex items-center gap-2.5 col-md-9">
                        <div className="img_sec lightbluebg">
                          <img
                            src={
                              item.ImageURL || "media/images/meal-default.png"
                            }
                            width={80}
                            alt="Meal"
                          />
                        </div>

                        <div className="flex flex-col gap-0.5">
                          <span className="text-2sm font-medium text-gray-900 cursor-pointer hover:text-primary mb-px">
                            {item.Detail || "Meal Option"}
                          </span>
                          <span className="text-2xs text-gray-700">
                            <i className="fa-solid fa-indian-rupee-sign"></i>{" "}
                            {item.Amount}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 col-md-3 my-3 my-md-0">
                        <div className="PlusMinusRow_adon">
                          <a
                            type="button"
                            className={`sub_adon subanc ${
                              getCurrentLegMealCounts()[index] === 0
                                ? "opacity-50 pointer-events-none"
                                : ""
                            }`}
                            onClick={() => handleMealSubtract(index)}
                          >
                            <span>-</span>
                          </a>
                          <input
                            type="text"
                            value={getCurrentLegMealCounts()[index]}
                            readOnly
                            className="PlusMinus_number_adon"
                          />
                          <a
                            type="button"
                            className={`add_adon addanc ${
                              totalMealSelected >= totalTravellers
                                ? "opacity-50 pointer-events-none"
                                : ""
                            }`}
                            onClick={() => handleMealAdd(index)}
                          >
                            <span>+</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoDataFlightCard
                  headingText="No Meal Found"
                  icon={
                    <FastfoodIcon className="no-data-color me-2 mr-2 flex-shrink-0 mt-0.5" />
                  }
                  paragraph=""
                />
              )}
            </div>
          </CustomTabPanel>
        </Box>
      </div>
    </div>
  );
};

export default FlightSeatInfo;
